import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { animated, useSpring } from '@react-spring/web';
import { interpolate as d3interpolate } from 'd3';

import { Comp, useTheme } from '@redsift/design-system';

import { ArcProps } from './types';
import { StyledArc } from './styles';
import { monochrome } from '../../scheme';
import { config } from '../../config';

const COMPONENT_NAME = 'Arc';
const CLASSNAME = 'redsift-arc';

export const Arc: Comp<ArcProps, SVGGElement> = forwardRef((props, ref) => {
  const { color = monochrome, data, isSelected: propsIsSelected, onClick, role } = props;

  const {
    className,
    createArc,
    disableAnimations,
    hasStroke,
    previousData = {
      data: {
        key: '',
        value: 0,
      },
      startAngle: 0,
      endAngle: 0,
      padAngle: 0,
      value: 0,
      index: 0,
    },
    ...forwardedProps
  } = props;

  const theme = useTheme();

  const interpolator = d3interpolate(previousData, data);

  const isSelectable = role === 'option';
  const isDeselected = isSelectable && propsIsSelected === false;

  const animatedProps = useSpring({
    ...config,
    to: async (next: (props?: object) => Promise<void>) => {
      await next({ t: 1 });
    },
    from: { t: 0 },
  });

  return (
    <StyledArc
      {...forwardedProps}
      ref={ref}
      className={classNames(Arc.className, className)}
      $clickable={Boolean(onClick)}
      $hasStroke={Boolean(hasStroke)}
      $theme={theme}
      tooltipProps={{ placement: 'client-point', offset: 12 }}
    >
      <animated.path
        d={
          config.immediate || disableAnimations
            ? (createArc(data) as unknown as string)
            : (animatedProps.t.to((t) => createArc(interpolator(t))) as unknown as string)
        }
        fill={isDeselected ? 'var(--redsift-color-neutral-light-grey)' : color}
      />
    </StyledArc>
  );
});
Arc.className = CLASSNAME;
Arc.displayName = COMPONENT_NAME;
